import { useState, useEffect, useRef, useCallback } from "react";

const EIGHT_HOURS = 8 * 60 * 60 * 1000;
const WARNING_LEAD = 5 * 60 * 1000; // show warning 5 min before logout

/**
 * Inactivity timer with warning dialog support.
 * - 8-hour timeout (resets on mouse/key/click/scroll/touch)
 * - Shows a warning 5 min before logout
 * - Skips entirely when "Remember Me" is active (vrg_remember_me in localStorage)
 */
export function useInactivityTimer(onTimeout: () => void) {
  const [showWarning, setShowWarning] = useState(false);
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const logoutRef = useRef<ReturnType<typeof setTimeout>>();
  const rememberMe = typeof window !== "undefined" && localStorage.getItem("vrg_remember_me") === "true";

  const clearTimers = useCallback(() => {
    if (warningRef.current) clearTimeout(warningRef.current);
    if (logoutRef.current) clearTimeout(logoutRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    clearTimers();
    if (rememberMe) return;

    warningRef.current = setTimeout(() => setShowWarning(true), EIGHT_HOURS - WARNING_LEAD);
    logoutRef.current = setTimeout(() => onTimeout(), EIGHT_HOURS);
  }, [onTimeout, rememberMe, clearTimers]);

  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (rememberMe) return;

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const handler = () => {
      // Only reset if the warning isn't showing (once warning shows, user must interact with dialog)
      if (!warningRef.current) return;
      resetTimer();
    };
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [resetTimer, rememberMe, clearTimers]);

  return { showWarning, dismissWarning };
}
